(() => {
  if (window.XEngineersBannerCarouselLoaded) return;
  window.XEngineersBannerCarouselLoaded = true;

  class XEngineersBannerCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.viewport = this.querySelector('[data-xe-carousel-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-xe-carousel-slide]'));
      this.previousButton = this.querySelector('[data-xe-carousel-previous]');
      this.nextButton = this.querySelector('[data-xe-carousel-next]');
      this.toggleButton = this.querySelector('[data-xe-carousel-toggle]');
      this.dots = Array.from(this.querySelectorAll('[data-xe-carousel-dot]'));
      this.status = this.querySelector('[data-xe-carousel-status]');
      this.currentIndex = 0;
      this.scrollFrame = null;
      this.timer = null;
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.pointerPaused = false;
      this.editorPaused = false;
      this.dragging = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.dragStartIndex = 0;
      this.visible = true;
      this.designMode = this.dataset.designMode === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.interval = Math.max(Number(this.dataset.autoplaySpeed) || 5000, 3000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.viewport || this.slides.length === 0) return;

      this.onScroll = this.handleScroll.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onMouseEnter = () => {
        if (!this.pauseOnHover) return;
        this.hoverPaused = true;
        this.syncAutoplay();
      };
      this.onMouseLeave = () => {
        this.hoverPaused = false;
        this.syncAutoplay(true);
      };
      this.onFocusIn = (event) => {
        const interactiveTarget = event.target?.closest?.('a, button:not(.xe-hero__slide-dot), input, select, textarea, [contenteditable="true"]');
        this.focusPaused = Boolean(interactiveTarget);
        this.syncAutoplay();
      };
      this.onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncAutoplay(true);
      };
      this.onPointerDown = (event) => {
        this.pointerPaused = true;
        this.syncAutoplay();

        const interactiveTarget = event.target?.closest?.('a, button, input, select, textarea');
        if (event.pointerType !== 'mouse' || event.button !== 0 || interactiveTarget) return;

        this.dragging = true;
        this.dragStartX = event.clientX;
        this.dragStartScrollLeft = this.viewport.scrollLeft;
        this.dragStartIndex = this.currentIndex;
        this.dataset.dragging = 'true';
        this.viewport.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      };
      this.onPointerMove = (event) => {
        if (!this.dragging) return;
        const dragDistance = this.dragStartX - event.clientX;
        this.viewport.scrollLeft = this.dragStartScrollLeft + dragDistance;
        event.preventDefault();
      };
      this.onPointerUp = (event) => {
        this.pointerPaused = false;

        if (this.dragging) {
          const endX = Number.isFinite(event.clientX) ? event.clientX : this.dragStartX;
          const dragDistance = this.dragStartX - endX;
          const threshold = Math.min(120, this.viewport.clientWidth * 0.12);

          this.dragging = false;
          delete this.dataset.dragging;
          if (this.viewport.hasPointerCapture?.(event.pointerId)) {
            this.viewport.releasePointerCapture(event.pointerId);
          }

          if (Math.abs(dragDistance) >= threshold) {
            this.goTo(this.dragStartIndex + (dragDistance > 0 ? 1 : -1), true);
          } else {
            this.goTo(this.getClosestIndex(), true);
          }
          return;
        }

        this.syncAutoplay(true);
      };
      this.onVisibilityChange = () => this.syncAutoplay(true);
      this.onBlockSelect = (event) => {
        const slide = event.target.closest('[data-xe-carousel-slide]');
        if (!slide || !this.contains(slide)) return;
        this.editorPaused = true;
        this.syncAutoplay(true);
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.goTo(index, false, false);
      };
      this.onBlockDeselect = (event) => {
        const slide = event.target.closest('[data-xe-carousel-slide]');
        if (!slide || !this.contains(slide)) return;
        this.editorPaused = false;
        this.syncAutoplay(true);
      };

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
      this.toggleButton?.addEventListener('click', () => this.toggleAutoplay());
      this.dots.forEach((dot, fallbackIndex) => {
        const dotIndex = Number(dot.dataset.xeCarouselDot);
        dot.addEventListener('click', () => this.goTo(Number.isInteger(dotIndex) ? dotIndex : fallbackIndex, true));
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0]?.isIntersecting ?? true;
            this.syncAutoplay(true);
          },
          { threshold: 0.05 }
        );
        this.observer.observe(this);
      }

      this.updateState(false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.observer?.disconnect();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const nextIndex = this.getClosestIndex();
        if (nextIndex !== this.currentIndex) {
          this.currentIndex = nextIndex;
          this.updateState(false);
        }
      });
    }

    getClosestIndex(scrollLeft = this.viewport.scrollLeft) {
      return this.slides.reduce((closestIndex, slide, index) => {
        const closestDistance = Math.abs(this.slides[closestIndex].offsetLeft - scrollLeft);
        const slideDistance = Math.abs(slide.offsetLeft - scrollLeft);
        return slideDistance < closestDistance ? index : closestIndex;
      }, 0);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1, true);
      }
    }

    goTo(index, announce = false, restart = true) {
      if (this.slides.length < 2) return;
      const targetIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = targetIndex;
      this.viewport.scrollTo({
        left: this.slides[targetIndex].offsetLeft,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
      this.updateState(announce);
      if (restart) this.syncAutoplay(true);
    }

    updateState(announce) {
      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });

      this.dots.forEach((dot, fallbackIndex) => {
        const dotIndex = Number(dot.dataset.xeCarouselDot);
        const active = (Number.isInteger(dotIndex) ? dotIndex : fallbackIndex) === this.currentIndex;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (announce && this.status) {
        this.status.textContent = `Banner ${this.currentIndex + 1} of ${this.slides.length}`;
      }

      this.dataset.activeSlide = String(this.currentIndex + 1);
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        !this.userPaused &&
        !this.hoverPaused &&
        !this.focusPaused &&
        !this.pointerPaused &&
        !this.editorPaused &&
        this.visible &&
        !document.hidden
      );
    }

    syncAutoplay(reset = false) {
      if (reset) window.clearTimeout(this.timer);
      if (!this.canAutoplay()) {
        window.clearTimeout(this.timer);
        this.timer = null;
        this.updateAutoplayButton();
        return;
      }

      if (!this.timer) {
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this.goTo(this.currentIndex + 1, false, false);
          this.syncAutoplay();
        }, this.interval);
      }
      this.updateAutoplayButton();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.syncAutoplay(true);
    }

    updateAutoplayButton() {
      const playing = this.canAutoplay();
      this.dataset.autoplayState = playing ? 'playing' : 'paused';
      if (!this.toggleButton) return;
      this.toggleButton.setAttribute('aria-label', playing ? 'Pause automatic banners' : 'Play automatic banners');
      this.toggleButton.setAttribute('aria-pressed', playing ? 'false' : 'true');
    }
  }

  if (!customElements.get('xengineers-banner-carousel')) {
    customElements.define('xengineers-banner-carousel', XEngineersBannerCarousel);
  }
})();
